import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useLocationStore } from '../../stores/locationStore';
import { useConfigStore } from '../../stores/configStore';
import { coordsToFrequency } from '../../lib/frequency';
import { subscribeRecentUsers } from '../../services/locationService';
import { formatDistance } from '../../lib/distance';
import type { NearbyUser } from '../../types/location';
import { colors } from '../../styles/colors';
import { DefaultAvatar } from '../../components/DefaultAvatar';

type Tab = 'nearby' | 'all';

export default function NearbyPage() {
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [tab, setTab] = useState<Tab>('nearby');
  const [recentUsers, setRecentUsers] = useState<NearbyUser[]>([]);
  const user = useAuthStore((s) => s.user);
  const effectiveLocation = useLocationStore((s) => s.effectiveLocation);
  const radarRadius = useLocationStore((s) => s.radarRadius);
  const config = useConfigStore((s) => s.config);

  const maxUsers = config.nearbyUsersLimit ?? 50;

  // Subscribe to recent users (no time filter, sorted by last activity)
  useEffect(() => {
    if (!user || !effectiveLocation) return;
    const blocked = new Set(user.blockedUserIds ?? []);
    const unsub = subscribeRecentUsers(
      user.uid, effectiveLocation.lat, effectiveLocation.lng,
      (users) => setRecentUsers(users.filter((u) => !blocked.has(u.uid))),
      maxUsers,
    );
    return unsub;
  }, [user?.uid, user?.blockedUserIds, effectiveLocation?.lat, effectiveLocation?.lng, maxUsers]);

  // "AI Bot 표시" off → bots disappear from the 주변/전체 lists as well as the map.
  const hideBots = user?.rejectBotMessages === true;
  const visibleRecentUsers = hideBots ? recentUsers.filter((u) => !u.isBot) : recentUsers;

  // "내 주변" = within radar range, sorted by most recent activity
  const nearbyUsers = visibleRecentUsers
    .filter((u) => (u.distance ?? Infinity) <= radarRadius * 1000)
    .sort((a, b) => (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0));

  // "전체" = all recent users, sorted by most recent activity
  const displayUsers = tab === 'nearby' ? nearbyUsers : visibleRecentUsers;

  const handleProfile = (u: NearbyUser, e?: React.MouseEvent) => {
    const distStr = u.distance != null ? (u.distance >= 1000 ? `${Math.round(u.distance / 1000)}km` : `${Math.round(u.distance)}m`) : '';
    const freq = (u.lat && u.lng) ? coordsToFrequency(u.lat, u.lng) : undefined;
    window.dispatchEvent(new CustomEvent('open-user-menu', { detail: { uid: u.uid, nickname: u.nickname, distance: distStr, frequency: freq, hideJump: true, x: e?.clientX, y: e?.clientY } }));
  };
  const handleDM = (u: NearbyUser) => {
    window.dispatchEvent(new CustomEvent('open-dm-compose', { detail: { recipientId: u.uid, recipientNickname: u.nickname } }));
  };

  const formatTime = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : ts.toMillis ? new Date(ts.toMillis()) : new Date(ts);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return isKo ? '방금 전' : 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}${isKo ? '분 전' : 'm ago'}`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}${isKo ? '시간 전' : 'h ago'}`;
    return `${Math.floor(diff / 86400000)}${isKo ? '일 전' : 'd ago'}`;
  };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <h1 className="text-base font-bold" style={{ color: colors.text }}>
          {isKo ? '최근 사용자' : 'Recent Users'}
        </h1>
        <span className="text-xs" style={{ color: colors.textMuted }}>
          {isKo ? `최근 ${visibleRecentUsers.length}명` : `Recent ${visibleRecentUsers.length}`}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex" style={{ borderBottom: `1px solid ${colors.border}` }}>
        {([
          { key: 'nearby' as Tab, label: isKo ? '내 주변 사용자' : 'Nearby' },
          { key: 'all' as Tab, label: isKo ? '전체 사용자' : 'All Users' },
        ]).map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex-1 py-2.5 text-center text-sm font-semibold"
            style={{
              color: tab === key ? colors.primary : colors.textMuted,
              borderBottom: `2px solid ${tab === key ? colors.primary : 'transparent'}`,
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* User list */}
      <div className="flex-1 overflow-y-auto">
        {displayUsers.length === 0 ? (
          <p className="mt-12 text-center text-sm" style={{ color: colors.textMuted }}>
            {t('nearby.noUsers')}
          </p>
        ) : (
          displayUsers.map((u) => {
            const gc = u.gender === 'male' ? '#4a9eff' : u.gender === 'female' ? '#ff6b9d' : colors.textMuted;
            return (
              <div key={u.uid} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/[0.03]"
                style={{ borderBottom: `1px solid ${colors.border}` }}>
                <div className="relative">
                  <button onClick={(e) => handleProfile(u, e)}
                    className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full"
                    style={{ backgroundColor: colors.surface, border: `2px solid ${gc}` }}>
                    {u.profileImageUrl
                      ? <img src={u.profileImageUrl} alt="" className="h-full w-full object-cover" />
                      : <DefaultAvatar gender={u.gender} size={24} />}
                  </button>
                  {u.isBot && config.showBotBadge !== false && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[7px] font-bold text-white"
                      style={{ backgroundColor: '#9333ea', border: '1.5px solid ' + colors.surface }}>B</span>
                  )}
                </div>

                <button onClick={(e) => handleProfile(u, e)} className="flex-1 overflow-hidden text-left">
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: gc }} />
                    <span className="truncate text-sm font-bold" style={{ color: colors.text }}>{u.nickname}</span>
                    {u.isBot && config.showBotBadge !== false && <span className="rounded bg-purple-600/30 px-1 text-[8px] font-bold text-purple-400">BOT</span>}
                    {u.isOnline && !(u.isBot && config.showBotBadge !== false) && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
                  </div>
                  <p className="text-[11px]" style={{ color: colors.textMuted }}>
                    {formatTime(u.updatedAt)}
                  </p>
                </button>

                <div className="shrink-0 text-right">
                  <span className="text-xs font-bold" style={{ color: colors.textSecondary }}>
                    {u.distance != null ? formatDistance(u.distance) : ''}
                  </span>
                </div>

                <button onClick={() => handleDM(u)}
                  className="shrink-0 rounded px-2 py-1 text-[10px] font-semibold text-white"
                  style={{ backgroundColor: colors.primary }}>
                  DM
                </button>
              </div>
            );
          })
        )}

        <div className="py-4 text-center">
          <p className="text-[10px]" style={{ color: colors.textMuted }}>
            &copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{isKo ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
